/**
 * Lanes for the commit graph.
 *
 * The host hands over commits with their parents, newest first; this turns
 * that into something drawable: which column each commit's node sits in, the
 * vertical lane lines crossing each row, and the short rounded elbows where a
 * branch leaves a lane or comes back into one. It is the standard sweep —
 * every lane waits for a particular commit, a commit takes the lane that was
 * waiting for it, its first parent inherits that lane and any other parent
 * opens one — with three rules that keep it honest and narrow:
 *
 * - A lane stays in its own column from top to bottom. Several lanes may wait
 *   for the same commit (a branch and the trunk it forked from); each keeps
 *   its column until that commit's row, where it bends into the node. The
 *   old drawing routed every such line to the *first* lane waiting, every
 *   row, which is what drew a fan of short stubs that ended nowhere.
 * - A lane frees the moment its branch ends, and the next new branch takes
 *   the leftmost free column — so the width is the number of branches open
 *   at once, not the number there ever were.
 * - A branch keeps its colour for as long as it lives: colours are given out
 *   when a lane opens, not by column.
 *
 * A row's lines are halves and wholes of a lane (`top`: down into the node,
 * `bottom`: out of the node, `full`: straight past it) and elbows (`in`: a
 * lane bending into the node, `out`: the node bending out to a lane), each
 * naming the child and parent commit it joins, so a renderer can highlight
 * one commit's path and tests can check that every line that leaves a row
 * arrives in the next.
 *
 * Pure and DOM-free so the shape can be tested without a repository.
 */

export interface LayoutInput {
  hash: string;
  parents: readonly string[];
  /** Drawn dashed with everything it leads down to its parent: the Working Changes rows. */
  dashed?: boolean;
}

/** A vertical piece of one lane within one row. */
export interface LaneLine {
  lane: number;
  colour: number;
  /** `top`: the row's top edge to its node; `bottom`: the node to the bottom edge; `full`: edge to edge. */
  part: 'top' | 'bottom' | 'full';
  /** The commit above the line … */
  child: string;
  /** … and the one it leads to. */
  parent: string;
  dashed?: boolean;
}

/**
 * A short rounded bend between a lane and the row's node. `in`: from the top
 * of lane `from`, turning into the node at `to` (a branch that started at this
 * commit comes home). `out`: from the node at `from`, turning down into lane
 * `to` (a merge's other parent, or a branch to the side).
 */
export interface LaneElbow {
  kind: 'in' | 'out';
  from: number;
  to: number;
  colour: number;
  child: string;
  parent: string;
  dashed?: boolean;
}

export interface GraphRow {
  hash: string;
  /** Column the commit's node sits in. */
  lane: number;
  /** The node's colour: its lane's. */
  colour: number;
  lines: LaneLine[];
  elbows: LaneElbow[];
  /** No parent at all: the first commit. Its lane ends here. */
  root: boolean;
  /** Nothing above leads into this commit: the tip of a branch (or the newest commit). */
  tip: boolean;
}

export interface GraphLayout {
  rows: GraphRow[];
  /** Widest the graph gets, for sizing the column. */
  laneCount: number;
}

interface Lane {
  /** The commit this lane is drawn down to. */
  waiting: string;
  colour: number;
  /** The commit the line leaves from. */
  child: string;
  dashed: boolean;
}

/** The leftmost column nobody occupies, not counting `avoid`; a new one at the right when all are taken. */
function freeColumn(lanes: (Lane | undefined)[], avoid: ReadonlySet<number> = new Set()): number {
  for (let i = 0; i < lanes.length; i++) if (!lanes[i] && !avoid.has(i)) return i;
  // Every free column was freed in this very row: take the first beyond them.
  let i = lanes.length;
  while (avoid.has(i)) i++;
  return i;
}

export function layoutCommitGraph(commits: readonly LayoutInput[]): GraphLayout {
  const lanes: (Lane | undefined)[] = [];
  const rows: GraphRow[] = [];
  let laneCount = 0;
  let nextColour = 0;

  for (const commit of commits) {
    const lines: LaneLine[] = [];
    const elbows: LaneElbow[] = [];
    const widthBefore = lanes.length;

    // The node's column: the first lane waiting for it, else a free one (a branch tip).
    let lane = lanes.findIndex((l) => l?.waiting === commit.hash);
    const tip = lane < 0;
    let colour: number;
    if (tip) {
      lane = freeColumn(lanes);
      colour = nextColour++;
    } else {
      const own = lanes[lane]!;
      colour = own.colour;
      lines.push({ lane, colour, part: 'top', child: own.child, parent: commit.hash, ...(own.dashed ? { dashed: true } : {}) });
    }

    // Every other lane waiting for this commit bends into the node and is free below it.
    const freed = new Set<number>();
    lanes.forEach((l, i) => {
      if (!l || i === lane) return;
      if (l.waiting === commit.hash) {
        elbows.push({ kind: 'in', from: i, to: lane, colour: l.colour, child: l.child, parent: commit.hash, ...(l.dashed ? { dashed: true } : {}) });
        lanes[i] = undefined;
        freed.add(i);
      } else {
        lines.push({ lane: i, colour: l.colour, part: 'full', child: l.child, parent: l.waiting, ...(l.dashed ? { dashed: true } : {}) });
      }
    });

    // The first parent inherits the column, which is what keeps a branch straight.
    const [first, ...others] = commit.parents;
    const dashed = commit.dashed === true;
    while (lanes.length <= lane) lanes.push(undefined);
    if (first) {
      lanes[lane] = { waiting: first, colour, child: commit.hash, dashed };
      lines.push({ lane, colour, part: 'bottom', child: commit.hash, parent: first, ...(dashed ? { dashed: true } : {}) });
    } else {
      lanes[lane] = undefined;
    }

    // Any other parent: into the lane already waiting for it, or a new lane to the side.
    for (const parent of others) {
      let to = lanes.findIndex((l) => l?.waiting === parent);
      let laneColour: number;
      if (to >= 0) {
        laneColour = lanes[to]!.colour;
      } else {
        // A column freed by this row's own `in` elbows would cross them; take another.
        to = freeColumn(lanes, freed);
        laneColour = nextColour++;
        while (lanes.length <= to) lanes.push(undefined);
        lanes[to] = { waiting: parent, colour: laneColour, child: commit.hash, dashed };
      }
      elbows.push({ kind: 'out', from: lane, to, colour: laneColour, child: commit.hash, parent, ...(dashed ? { dashed: true } : {}) });
    }

    while (lanes.length > 0 && lanes[lanes.length - 1] === undefined) lanes.pop();
    rows.push({ hash: commit.hash, lane, colour, lines, elbows, root: commit.parents.length === 0, tip });
    laneCount = Math.max(laneCount, widthBefore, lanes.length, lane + 1);
  }

  return { rows, laneCount };
}

/**
 * Which lanes carry a line across a row's bottom edge, and which across its
 * top edge — with the commit each line leads to. A layout is whole when what
 * leaves one row's bottom is exactly what enters the next row's top; the tests
 * hold every shape to that.
 */
export function rowEdges(row: GraphRow): { top: Map<number, string>; bottom: Map<number, string> } {
  const top = new Map<number, string>();
  const bottom = new Map<number, string>();
  for (const line of row.lines) {
    if (line.part !== 'bottom') top.set(line.lane, line.parent);
    if (line.part !== 'top') bottom.set(line.lane, line.parent);
  }
  for (const elbow of row.elbows) {
    if (elbow.kind === 'in') top.set(elbow.from, elbow.parent);
    else bottom.set(elbow.to, elbow.parent);
  }
  return { top, bottom };
}

/** How many lane colours there are; `--git-lane-<n>` in index.css carries each, for light and dark. */
export const LANE_COLOUR_COUNT = 10;

/**
 * A lane's colour, as the CSS variable the theme defines. Deliberately not the
 * semantic tokens: these are identity colours for lines, and a branch that
 * happened to get the fourth colour must not read as an error.
 */
export function laneColour(colour: number): string {
  return `var(--git-lane-${((colour % LANE_COLOUR_COUNT) + LANE_COLOUR_COUNT) % LANE_COLOUR_COUNT})`;
}
